import React, { useState, useCallback } from 'react';
import { getInfraStatus, InfraStatus, ApiError } from '../services/api';
import '../styles/InfraStatusPanel.css';

/**
 * Collapsible live infrastructure panel backed by the Provisioning Agent.
 * Requires a signed-in Cognito session (the API attaches the JWT).
 */
const InfraStatusPanel: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<InfraStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getInfraStatus();
      setStatus(data);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError('Session expired — please sign in again to view infrastructure status.');
      } else {
        setError(err instanceof Error ? err.message : 'Failed to load infrastructure status');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !status && !loading) load();
  };

  return (
    <div className="infra-panel">
      <button type="button" className="infra-toggle" onClick={toggle}>
        <span className="infra-title">
          <span className={`chevron ${open ? 'open' : ''}`}>▸</span>
          Infrastructure Status
          <span className="infra-source">Provisioning Agent</span>
        </span>
        <span className="infra-summary">
          {loading
            ? 'Loading…'
            : status
              ? `${status.servers?.length || 0} servers · ${status.ports?.length || 0} ports allocated`
              : 'Click to load'}
        </span>
      </button>

      {open && (
        <div className="infra-body">
          {error && (
            <div className="infra-error">
              {error}
              <button type="button" onClick={load} className="infra-retry">Retry</button>
            </div>
          )}

          {loading && !status && <div className="infra-loading">Checking servers…</div>}

          {status && (
            <>
              <div className="infra-servers">
                {status.servers?.map(srv => (
                  <div key={srv.server} className={`infra-server ${srv.status === 'online' ? 'online' : 'offline'}`}>
                    <div className="server-head">
                      <span className="server-dot" />
                      <strong>{srv.server}</strong>
                    </div>
                    <span className="server-ip">{srv.ip}</span>
                    <span className="server-status">
                      {srv.status === 'online' ? 'Online' : 'Unreachable'}
                      {Array.isArray(srv.pm2Processes) && srv.pm2Processes.length > 0 &&
                        ` · ${srv.pm2Processes.length} PM2 processes`}
                    </span>
                  </div>
                ))}
              </div>

              {status.ports && status.ports.length > 0 && (
                <div className="infra-ports">
                  <span className="ports-label">Port allocation</span>
                  <div className="ports-list">
                    {status.ports.map(p => (
                      <span key={p.port} className="port-chip">
                        :{p.port} <em>{p.service || p.appId || 'free'}</em>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <button type="button" className="infra-refresh" onClick={load} disabled={loading}>
                {loading ? 'Refreshing…' : '↻ Refresh'}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default InfraStatusPanel;
